<script setup lang="ts">
// Structured add/remove rows for a task version's successors, with a JSON toggle for power users.
// Every field is the kit's (#1227): the number fields the kit `Input`, the task, outcome and condition pickers the kit
// `Select`, and each stored row sits on a kit surface. It states no border, fill or radius of its own.
//
// A successor reads "When this finishes with ‹outcome›, then run ‹task›" (#1754). The task is picked by name from the
// enterprise's tasks (`tasks`) and the outcome from the list the API serves (`outcomes`), both given by the screen:
// there is no free-text slug or outcome, and the JSON toggle stays the escape hatch for anything outside the lists.

import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, ZhPanel } from '@zerohuman/ui'
import { computed, ref, useId } from 'vue'
import type { TaskSuccessor } from '@/api'
import RowsJsonSection from '@/components/RowsJsonSection.vue'
import { useRowsModel } from '@/composables/useRowsModel'
import type { SuccessorTaskChoice } from '@/composables/useSuccessorChoices'
import { pickedId } from '@/lib/memberPickers'

/**
 * Rows hold what the inputs store, and `v-model` on a `type="number"` input stores a number, so the two
 * caps are not always strings. `toValue` puts the numbers and the omitted optionals back.
 */
type SuccessorRow = {
    slug: string
    on: string
    when: string
    delayMinutes: string | number
    maxRunsPerIssue: string | number
}

/** The conditions the OS matches before starting a successor; blank means "always". */
const WHEN_OPTIONS = ['has_issue', 'has_pr', 'no_pr']

/**
 * The condition list's "always" choice. A blank condition means always, as it always has, but the kit Select (Reka)
 * refuses an item whose value is the empty string, so the list offers it under this value and choosing it blanks
 * the condition again. Conditions are lowercase words joined by underscores, so none can be mistaken for it.
 */
const ALWAYS = ':always'

/**
 * The outcome an edge with no `on` fires on. It is the outcome picker's default, and a new edge waiting on it is
 * stored with no `on`, the shape such an edge has always had. A stored edge that names it keeps naming it.
 */
const DEFAULT_OUTCOME = 'succeeded'

/** What the condition picker holds for a row's condition: the condition, or the "always" stand-in when blank. */
function whenChoice(when: string): string {
    return when || ALWAYS
}

/** The condition a picker's choice stores: blank for "always", the condition otherwise. */
function whenOf(choice: unknown): string {
    const picked = pickedId(choice)
    return picked === ALWAYS ? '' : picked
}

/** The kit Select's trigger is a button, so it is named by the words beside it rather than wrapped in a label. */
const fieldId = useId()

/**
 * The successor slug that is not a task (issue #784): it sends the outcome to the return target the run carries,
 * which only exists at runtime. On a task version there is no run, so the row says where it goes in words.
 */
const RETURN_SLUG = '$return'

/**
 * The range each numeric field advertises. The `min`/`max` attributes below are bound to these, and
 * `numberOrNone` enforces the same bounds, because the browser only checks them on form submission and
 * "Add successor" is a plain button click.
 */
const DELAY_BOUNDS = { min: 1, max: 1440 }
const CAP_BOUNDS = { min: 1 }

const props = withDefaults(
    defineProps<{
        modelValue: TaskSuccessor[]
        /** The enterprise's tasks the "then run" picker offers, by name. */
        tasks?: SuccessorTaskChoice[]
        /** The outcomes the "When this finishes with" picker offers, as the API serves them (`succeeded` first). */
        outcomes?: string[]
    }>(),
    { tasks: () => [], outcomes: () => [] },
)
const emit = defineEmits<{ 'update:modelValue': [TaskSuccessor[]] }>()

const { rows, showJson, jsonText, jsonError, append, removeAt, toggleJson } = useRowsModel<SuccessorRow, TaskSuccessor>(
    {
        modelValue: () => props.modelValue,
        emit: value => emit('update:modelValue', value),
        toRow: successor => ({
            slug: successor.slug,
            on: successor.on ?? '',
            when: successor.when ?? '',
            delayMinutes: numberText(successor.delayMinutes),
            maxRunsPerIssue: numberText(successor.maxRunsPerIssue),
        }),
        toValue: row => {
            const successor: TaskSuccessor = { slug: row.slug.trim() }
            if (row.on.trim()) {
                successor.on = row.on.trim()
            }
            if (row.when.trim()) {
                successor.when = row.when.trim()
            }
            const delayMinutes = numberOrNone(row.delayMinutes, DELAY_BOUNDS)
            if (delayMinutes !== undefined) {
                successor.delayMinutes = delayMinutes
            }
            const maxRunsPerIssue = numberOrNone(row.maxRunsPerIssue, CAP_BOUNDS)
            if (maxRunsPerIssue !== undefined) {
                successor.maxRunsPerIssue = maxRunsPerIssue
            }
            return successor
        },
    },
)

const draft = ref<SuccessorRow>(emptyRow())

/**
 * The outcomes offered, in the order they are given. The default is always one of them, so the picker never holds a
 * choice it does not list, even before the served list has arrived.
 */
const outcomeChoices = computed(() =>
    props.outcomes.includes(DEFAULT_OUTCOME) ? props.outcomes : [DEFAULT_OUTCOME, ...props.outcomes],
)

/** A stored edge's task by name. A slug that names none of the enterprise's tasks is shown as it is stored. */
function taskName(slug: string): string {
    return props.tasks.find(task => task.slug === slug)?.name ?? slug
}

function numberText(value: number | undefined): string {
    return value === undefined || value === null ? '' : String(value)
}

/**
 * A cap left blank is not a cap: the OS applies its own default rather than a zero. Text that is not a
 * number is dropped the same way, so a hand-pasted `"soon"` never reaches save() as a `NaN` that
 * `JSON.stringify` would quietly send as `null`. A number outside the field's own `min`/`max` is dropped
 * too: nothing else rejects it, since a click on "Add successor" never runs the browser's range check.
 */
function numberOrNone(value: string | number, bounds: { min: number; max?: number }): number | undefined {
    const text = String(value).trim()
    if (!text) {
        return undefined
    }
    const parsed = Number(text)
    if (Number.isNaN(parsed) || parsed < bounds.min) {
        return undefined
    }
    return bounds.max !== undefined && parsed > bounds.max ? undefined : parsed
}

function emptyRow(): SuccessorRow {
    return { slug: '', on: DEFAULT_OUTCOME, when: '', delayMinutes: '', maxRunsPerIssue: '' }
}

/**
 * The picked task names the task this edge starts; without one there is no edge. An edge waiting on the default
 * outcome is stored with no `on`, as it always has been.
 */
function add(): void {
    const slug = draft.value.slug.trim()
    if (!slug) {
        return
    }
    append({ ...draft.value, slug, on: draft.value.on === DEFAULT_OUTCOME ? '' : draft.value.on })
    draft.value = emptyRow()
}
</script>

<template>
	<RowsJsonSection
		testid="task-successors"
		title="Successors"
		:show-json="showJson"
		:json-text="jsonText"
		:json-error="jsonError"
		:json-rows="6"
		@update:json-text="jsonText = $event"
		@toggle="toggleJson"
	>
		<template #description>What runs next, and on which outcome.</template>
		<p v-if="!rows.length" class="text-muted-foreground text-sm">Nothing runs after this task yet.</p>
		<ul class="space-y-2">
			<ZhPanel
				v-for="(row, index) in rows"
				:key="index"
				as="li"
				data-testid="task-successor-row"
				class="flex flex-wrap items-end gap-2"
			>
				<p class="basis-full text-sm">
					When this finishes with <code class="font-mono text-xs">{{ row.on || DEFAULT_OUTCOME }}</code>,
					<!-- #784: `$return` names no task; it sends the outcome to whoever asked for this run's work. -->
					<template v-if="row.slug === RETURN_SLUG">hand the outcome back to the caller</template>
					<template v-else>then run <span class="font-medium">{{ taskName(row.slug) }}</span></template>
				</p>
				<div class="grid gap-1 text-xs">
					<span :id="`${fieldId}-when-${index}`">Only when</span>
					<Select :model-value="whenChoice(row.when)" @update:model-value="row.when = whenOf($event)">
						<SelectTrigger :aria-labelledby="`${fieldId}-when-${index}`">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem :value="ALWAYS">always</SelectItem>
							<SelectItem v-for="option in WHEN_OPTIONS" :key="option" :value="option">{{ option }}</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<label class="grid gap-1 text-xs">
					Delay (min)
					<Input
						v-model="row.delayMinutes"
						type="number"
						:min="DELAY_BOUNDS.min"
						:max="DELAY_BOUNDS.max"
						class="w-24"
					/>
				</label>
				<label class="grid gap-1 text-xs">
					Max runs / issue
					<Input v-model="row.maxRunsPerIssue" type="number" :min="CAP_BOUNDS.min" class="w-24" />
				</label>
				<Button
					type="button"
					size="sm"
					variant="destructive"
					data-testid="task-successor-remove"
					@click="removeAt(index)"
				>
					Remove
				</Button>
			</ZhPanel>
		</ul>
		<div class="flex flex-wrap items-end gap-2">
			<div class="grid gap-1 text-xs">
				<span :id="`${fieldId}-add-outcome`">When this finishes with</span>
				<Select
					:model-value="draft.on"
					data-testid="task-successor-add-outcome"
					@update:model-value="draft.on = pickedId($event)"
				>
					<SelectTrigger :aria-labelledby="`${fieldId}-add-outcome`">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem v-for="outcome in outcomeChoices" :key="outcome" :value="outcome">{{ outcome }}</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<div class="grid gap-1 text-xs">
				<span :id="`${fieldId}-add-task`">then run</span>
				<Select
					:model-value="draft.slug || undefined"
					data-testid="task-successor-add-task"
					@update:model-value="draft.slug = pickedId($event)"
				>
					<SelectTrigger :aria-labelledby="`${fieldId}-add-task`">
						<SelectValue placeholder="Choose a task…" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem :value="RETURN_SLUG">Back to the caller</SelectItem>
						<SelectItem v-for="task in tasks" :key="task.slug" :value="task.slug">{{ task.name }}</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<div class="grid gap-1 text-xs">
				<span :id="`${fieldId}-add-when`">Only when</span>
				<Select
					:model-value="whenChoice(draft.when)"
					data-testid="task-successor-add-when"
					@update:model-value="draft.when = whenOf($event)"
				>
					<SelectTrigger :aria-labelledby="`${fieldId}-add-when`">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem :value="ALWAYS">always</SelectItem>
						<SelectItem v-for="option in WHEN_OPTIONS" :key="option" :value="option">{{ option }}</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<label class="grid gap-1 text-xs">
				Delay (min)
				<Input
					v-model="draft.delayMinutes"
					data-testid="task-successor-add-delay"
					type="number"
					:min="DELAY_BOUNDS.min"
					:max="DELAY_BOUNDS.max"
					class="w-24"
				/>
			</label>
			<label class="grid gap-1 text-xs">
				Max runs / issue
				<Input
					v-model="draft.maxRunsPerIssue"
					data-testid="task-successor-add-cap"
					type="number"
					:min="CAP_BOUNDS.min"
					class="w-24"
				/>
			</label>
			<Button type="button" size="sm" variant="outline" data-testid="task-successor-add" @click="add">
				Add successor
			</Button>
		</div>
	</RowsJsonSection>
</template>
